import { useEffect, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLocalization } from "@/context/localization-context";
import { catalogWrites, classifyWriteError, useCatalogWrite, type WriteError } from "../api";
import { cleanIngredient, INGREDIENT_ID, isSauceClass, knownDetectorClasses, normalizeName, sameIngredient, slug } from "../model";
import type { CatalogIngredient } from "../types";
import { EditorFooter, Field, TagEditor } from "./shared";

/**
 * Creates an ingredient (``ingredient`` null) or edits one: the name the evaluation reports, the names
 * it goes by in recipes and orders, and the detector classes that see it (none = not checked).
 */
export function IngredientEditor({
  open,
  onOpenChange,
  ingredient,
  ingredients,
  usage,
  onReload,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ingredient: CatalogIngredient | null;
  ingredients: CatalogIngredient[];
  /** how many recipes use it */
  usage: number;
  onReload: () => Promise<unknown>;
}) {
  const { t, tp } = useLocalization();
  const write = useCatalogWrite();
  const isNew = ingredient === null;

  const [id, setId] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const [name, setName] = useState("");
  const [aliases, setAliases] = useState<string[]>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [minCount, setMinCount] = useState("");
  const [minShare, setMinShare] = useState("");
  const [base, setBase] = useState<string | null>(null);
  const [error, setError] = useState<WriteError | null>(null);
  const [resetPending, setResetPending] = useState(false);

  const reset = (from: CatalogIngredient | null) => {
    setId(from?.id ?? "");
    setIdTouched(false);
    setName(from?.name ?? "");
    setAliases(from?.aliases ?? []);
    setClasses(from?.detector_classes ?? []);
    setMinCount(from?.extra_min_count != null ? String(from.extra_min_count) : "");
    setMinShare(from?.extra_min_share != null ? String(Number((from.extra_min_share * 100).toFixed(2))) : "");
    setBase(from?.updated_at ?? null);
    setError(null);
  };

  useEffect(() => {
    if (open) reset(ingredient);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (resetPending && ingredient && (ingredient.updated_at ?? null) !== base) {
      reset(ingredient);
      setResetPending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredient, resetPending]);

  const others = useMemo(() => ingredients.filter((i) => i.id !== ingredient?.id), [ingredients, ingredient]);
  const known = useMemo(() => knownDetectorClasses(ingredients), [ingredients]);
  // extra ingredients: empty = the evaluation setting (pieces) / any share (sauces)
  const sauce = classes.length > 0 && classes.every(isSauceClass);
  const countValue = minCount.trim() === "" ? null : Number(minCount);
  const shareValue = minShare.trim() === "" ? null : Number(minShare.replace(",", ".")) / 100;
  const countProblem = countValue !== null && (!Number.isInteger(countValue) || countValue < 1 || countValue > 50);
  const shareProblem = shareValue !== null && (!Number.isFinite(shareValue) || shareValue < 0 || shareValue > 1);
  const draft = cleanIngredient({
    name,
    aliases,
    detector_classes: classes,
    extra_min_count: sauce || countProblem ? null : countValue,
    extra_min_share: !sauce || shareProblem ? null : shareValue === null ? null : Number(shareValue.toFixed(4)),
  });

  const effectiveId = isNew ? id.trim() : ingredient.id;
  const idProblem = !isNew
    ? null
    : !effectiveId
      ? t("catalog.ingredient.idMissing")
      : !INGREDIENT_ID.test(effectiveId)
        ? t("catalog.ingredient.idInvalid")
        : ingredients.some((i) => i.id === effectiveId)
          ? t("catalog.ingredient.idTaken")
          : null;
  // the backend refuses these too; shown while typing
  const clashes = useMemo(() => {
    const owner = new Map<string, string>();
    for (const other of others) for (const alias of other.aliases) owner.set(normalizeName(alias), other.name);
    return draft.aliases
      .filter((alias) => owner.has(normalizeName(alias)))
      .map((alias) => t("catalog.ingredient.aliasTaken", { alias, owner: owner.get(normalizeName(alias))! }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [others, draft.aliases.join("\u0000")]);
  const unknownClasses = draft.detector_classes.filter((c) => !known.includes(c));
  const unchanged =
    !isNew &&
    sameIngredient(draft, {
      name: ingredient.name,
      aliases: ingredient.aliases,
      detector_classes: ingredient.detector_classes,
      extra_min_count: ingredient.extra_min_count ?? null,
      extra_min_share: ingredient.extra_min_share ?? null,
    });
  const ruleProblem = sauce ? shareProblem : countProblem;
  const canSave = !!draft.name && !idProblem && clashes.length === 0 && !ruleProblem && !unchanged;

  const save = async () => {
    setError(null);
    try {
      await write.mutateAsync(
        isNew ? catalogWrites.createIngredient(effectiveId, draft) : catalogWrites.updateIngredient(ingredient.id, draft, base),
      );
      toast.success(t("catalog.ingredient.saved", { name: draft.name }));
      onOpenChange(false);
    } catch (e) {
      setError(classifyWriteError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !write.isPending && onOpenChange(next)}>
      <DialogContent className="max-w-xl gap-5">
        <DialogHeader className="text-left">
          <DialogTitle className="pr-6">
            {isNew ? t("catalog.ingredient.newTitle") : t("catalog.ingredient.editTitle", { name: ingredient.name })}
          </DialogTitle>
          <DialogDescription>
            {isNew ? t("catalog.ingredient.newDescription") : tp("catalog.ingredient.usage", usage)}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_11rem]">
          <Field label={t("catalog.ingredient.name")} htmlFor="ingredient-name" hint={t("catalog.ingredient.nameHint")}>
            <Input
              id="ingredient-name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (isNew && !idTouched) setId(slug(event.target.value));
              }}
              autoComplete="off"
            />
          </Field>
          <Field label={t("catalog.ingredient.id")} htmlFor="ingredient-id" hint={isNew ? idProblem ?? undefined : undefined}>
            <Input
              id="ingredient-id"
              value={isNew ? id : ingredient.id}
              disabled={!isNew}
              onChange={(event) => {
                setId(event.target.value);
                setIdTouched(true);
              }}
              className="font-mono"
              autoComplete="off"
            />
          </Field>
        </div>

        <Field label={t("catalog.ingredient.aliases")} htmlFor="ingredient-aliases" hint={t("catalog.ingredient.aliasesHint")}>
          <TagEditor id="ingredient-aliases" values={aliases} onChange={setAliases} placeholder={t("catalog.ingredient.aliasPlaceholder")} />
        </Field>
        {clashes.length > 0 && (
          <ul className="-mt-2 flex flex-col gap-0.5 text-xs text-red-300">
            {clashes.map((clash) => (
              <li key={clash}>{clash}</li>
            ))}
          </ul>
        )}

        <Field label={t("catalog.ingredient.classes")} htmlFor="ingredient-classes" hint={t("catalog.ingredient.classesHint")}>
          <TagEditor
            id="ingredient-classes"
            values={classes}
            onChange={setClasses}
            placeholder={t("catalog.ingredient.classPlaceholder")}
            suggestions={known}
            renderTag={(value) => (
              <span className="inline-flex items-center gap-1 font-mono">
                {!known.includes(value) && <AlertTriangle className="h-3 w-3 text-amber-400" aria-hidden />}
                {value}
              </span>
            )}
          />
        </Field>
        {unknownClasses.length > 0 && (
          <p className="-mt-2 flex items-start gap-1.5 text-xs text-amber-300">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            {t("catalog.ingredient.unknownClasses", { classes: unknownClasses.join(", ") })}
          </p>
        )}

        {classes.length > 0 &&
          (sauce ? (
            <Field
              label={t("catalog.ingredient.extraMinShare")}
              htmlFor="ingredient-extra-share"
              hint={shareProblem ? t("catalog.ingredient.extraMinShareInvalid") : t("catalog.ingredient.extraMinShareHint")}
            >
              <div className="relative w-32">
                <Input
                  id="ingredient-extra-share"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={100}
                  step={1}
                  value={minShare}
                  onChange={(event) => setMinShare(event.target.value)}
                  placeholder={t("catalog.ingredient.extraAny")}
                  className="h-9 pr-7 text-right tabular-nums"
                />
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
              </div>
            </Field>
          ) : (
            <Field
              label={t("catalog.ingredient.extraMinCount")}
              htmlFor="ingredient-extra-count"
              hint={countProblem ? t("catalog.ingredient.extraMinCountInvalid") : t("catalog.ingredient.extraMinCountHint")}
            >
              <Input
                id="ingredient-extra-count"
                type="number"
                inputMode="numeric"
                min={1}
                max={50}
                step={1}
                value={minCount}
                onChange={(event) => setMinCount(event.target.value)}
                placeholder={t("catalog.ingredient.extraDefault")}
                className="h-9 w-32 text-right tabular-nums"
              />
            </Field>
          ))}

        <EditorFooter
          error={error}
          onReload={() => {
            setResetPending(true);
            void onReload();
          }}
          saving={write.isPending}
          canSave={canSave}
          onCancel={() => onOpenChange(false)}
          onSave={() => void save()}
          hint={isNew ? t("catalog.ingredient.newHint") : undefined}
        />
      </DialogContent>
    </Dialog>
  );
}
